// Entra sign-in with MSAL Browser: auth code flow with PKCE, redirect (no pop-ups). The browser
// only ever holds a token for the Procure API scope; every decision is made by the broker.

import {
  createStandardPublicClientApplication,
  InteractionRequiredAuthError,
  type AccountInfo,
  type IPublicClientApplication,
} from '@azure/msal-browser';
import type { AuthConfigBody } from '../../shared/me';

export type AuthStart =
  { kind: 'notConfigured' } | { kind: 'signedOut' } | { kind: 'signedIn'; account: AccountInfo };

let pca: IPublicClientApplication | null = null;
let apiScope = '';

export async function startAuth(): Promise<AuthStart> {
  const res = await fetch('/api/config', { headers: { accept: 'application/json' } });
  if (!res.ok) throw new Error(`config ${res.status}`);
  const config = (await res.json()) as AuthConfigBody;
  if (!config.configured) return { kind: 'notConfigured' };

  apiScope = config.scope;
  const home = `${location.origin}/`;
  pca = await createStandardPublicClientApplication({
    auth: {
      clientId: config.clientId,
      authority: `https://login.microsoftonline.com/${config.tenantId}`,
      redirectUri: home,
      postLogoutRedirectUri: home,
    },
    // Session storage: tokens end with the tab, and are not shared with other tabs or sites.
    cache: { cacheLocation: 'sessionStorage' },
  });

  const result = await pca.handleRedirectPromise();
  const account = result?.account ?? pca.getActiveAccount() ?? pca.getAllAccounts()[0];
  if (!account) return { kind: 'signedOut' };
  pca.setActiveAccount(account);
  return { kind: 'signedIn', account };
}

function client(): IPublicClientApplication {
  if (!pca) throw new Error('Sign-in has not started');
  return pca;
}

export async function signIn(): Promise<void> {
  await client().loginRedirect({ scopes: [apiScope], prompt: 'select_account' });
}

export async function signOut(): Promise<void> {
  const c = client();
  await c.logoutRedirect({ account: c.getActiveAccount() });
}

/** An access token for the Procure API, renewed silently; falls back to a redirect. */
export async function accessToken(): Promise<string> {
  const c = client();
  const account = c.getActiveAccount();
  if (!account) throw new Error('Not signed in');
  try {
    return (await c.acquireTokenSilent({ scopes: [apiScope], account })).accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      await c.acquireTokenRedirect({ scopes: [apiScope], account });
    }
    throw err;
  }
}
